import { apiClient } from './client';

// S46: Treasury/Company Ledger interfaces
export interface TreasurySummary {
  kpis: {
    total_base_cents: number;
    total_withdrawable_cents: number;
    unsettled_profit_cents: number;
    company_balance_cents: number;
    // S47: Company float tracking
    company_float_cents: number;
    company_withdrawable_cents: number;
    company_float_cap_pct: number;
    company_float_cap_amount_cents: number;
    user_pool_value_cents: number;
    total_pool_value_cents: number;
    active_user_count: number;
    effective_fee_subsidy_rate_pct: number;
  };
  liquidity_planning: {
    approved_pending_withdrawals_cents: number;
    all_pending_withdrawals_cents: number;
    projected_next_settlement_to_withdrawable_cents: number;
    suggested_transfer_to_bank_cents: number;
    // S-E4: Liquidity metrics
    company_available_cents: number;
    pending_payouts_cents: number;
    available_liquidity_cents: number;
    liquidity_healthy: boolean;
    recommended_transfer_cents: number;
  };
  pool_composition: PoolCompositionUser[];
  // S-E2: Company payments readiness
  company_payments_readiness?: {
    ready: boolean;
    reasons: string[];
    messages: string[];
  };
}

// S-F2: Treasury operations interfaces
export interface PendingTransfer {
  id: string;
  user_id?: string;
  user_name?: string;
  user_email?: string;
  amount_cents: number;
  status: string;
  provider?: string;
  provider_transfer_id?: string;
  withdrawal_request_id?: string;
  created_at: string;
  updated_at?: string;
}

export interface ApprovedNotInitiated {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  amount_cents: number;
  approved_at: string;
  created_at: string;
}

export interface TreasuryOperations {
  pending_deposits_total_cents: number;
  pending_payouts_total_cents: number;
  approved_not_initiated_total_cents: number;
  expected_net_change_cents: number;
  pending_deposits: PendingTransfer[];
  pending_payouts: PendingTransfer[];
  approved_not_initiated: ApprovedNotInitiated[];
  last_updated_at: string;
}

export interface PoolCompositionUser {
  user_id: string;
  full_name: string;
  email: string;
  base_principal_cents: number;
  withdrawable_cents: number;
  profit_week_cents: number;
  one_time_withdrawable_amount_cents: number;
  eligible_for_earnings: boolean;
  participation_status?: string;
}

export interface CompanyLedgerEntry {
  id: string;
  occurred_at: string;
  kind: string;
  amount_cents: number;
  description: string;
  balance_after_cents: number;
  source_type?: string;
  source_id?: string;
  created_by_user_id?: string;
  created_by_user_name?: string;
  created_at: string;
}

export interface CompanyLedgerEntriesResponse {
  entries: CompanyLedgerEntry[];
  pagination: {
    page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
}

export const adminTreasuryEndpoints = {
  // Get treasury summary (KPIs, liquidity planning, pool composition)
  getSummary: () =>
    apiClient.get<TreasurySummary>('/api/admin/treasury/summary'),

  // S-F2: Get treasury operations (pending transfers, expected net change)
  getOperations: (params?: { window_days?: number }) =>
    apiClient.get<TreasuryOperations>('/api/admin/treasury/operations', { params }),

  // Get company ledger entries with filters
  getLedgerEntries: (params?: {
    start_date?: string;
    end_date?: string;
    page?: number;
    per?: number;
  }) =>
    apiClient.get<CompanyLedgerEntriesResponse>('/api/admin/treasury/company_ledger_entries', { params }),

  // Create manual adjustment
  createAdjustment: (data: {
    amount_cents: number;
    description: string;
    kind?: string;
  }) =>
    apiClient.post<{ entry: CompanyLedgerEntry; message: string }>(
      '/api/admin/treasury/company_ledger_entries/adjustment',
      data
    ),
};

// S-F4: Reconciliation interfaces
export interface ReconciliationMismatch {
  mismatch_type: 'pending_too_long' | 'missing_ledger_entry' | 'status_mismatch' | 'orphan_ledger_entry';
  transfer_id: string;
  transfer_type?: string;
  owner_type?: string;
  owner_id?: string;
  owner_name?: string;
  owner_email?: string;
  amount_cents: number;
  internal_status: string;
  provider_status?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  age_days: number;
  suggested_actions: string[];
  ledger_entry_id?: string;
}

export interface ReconciliationReport {
  mismatches: ReconciliationMismatch[];
  summary: {
    total_count: number;
    by_type: Record<string, number>;
    window_days: number;
    generated_at: string;
  };
}

export const adminReconciliationEndpoints = {
  // Get reconciliation report
  getReport: (params?: { window_days?: number; type?: string }) =>
    apiClient.get<ReconciliationReport>('/api/admin/reconciliation', { params }),

  // Refresh transfer status from provider
  refreshStatus: (transferId: string) =>
    apiClient.post<{ message: string; transfer: { id: string; status: string; updated_at: string } }>(
      `/api/admin/reconciliation/${transferId}/refresh_status`
    ),

  // Apply ledger mapping for completed transfer
  applyLedgerMapping: (transferId: string) =>
    apiClient.post<{ message: string; ledger_entry: { id: string; amount_cents: number; transfer_type: string; created: boolean } }>(
      `/api/admin/reconciliation/${transferId}/apply_ledger_mapping`
    ),
};
